import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { searchFashion } from '../api/rakuten_service';
import API_URL from '../config/api.js';
import TRENDING_CATEGORIES, { TRENDING_DISPLAY_NAMES } from '../constants/trendingCategories.js';
import { useNavigationStack } from '../contexts/NavigationContext';
import { handleImageError } from '../utils/imageUtils';
import HowItWorksDemo from '../components/HowItWorksDemo';
import TypewriterText from '../components/TypewriterText';
import DisclaimerBanner from '../components/DisclaimerBanner';
import SearchModeCard from '../components/SearchModeCard';
import './Home.css';

const Home = () => {
  const [trendingProducts, setTrendingProducts] = useState({});
  const [loadingTrending, setLoadingTrending] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const location = useLocation();
  const { navigateWithTransition } = useNavigationStack();


  // Handle scroll to section if passed via navigation state
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;

      // Wait for page to fully render before scrolling
      const timer = setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Clear the state to prevent re-scrolling on subsequent renders
        window.history.replaceState({}, document.title);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [location.state]);


  // Fetch trending products from backend
  useEffect(() => {
    const fetchTrendingProducts = async () => {
      const products = {};

      try {
        console.log('🔍 Fetching trending products from:', API_URL);
        // Fetch all categories in parallel for better performance (5x faster!)
        // Only fetch from Rakuten for Trending Styles (no CJ API call)
        const fetchPromises = TRENDING_CATEGORIES.map(async (category) => {
          try {
            const url = `${API_URL}/api/search_by_keyword?keyword=${category}&limit=1&source=rakuten`;
            console.log(`💬“¡ Fetching ${category} from:`, url);
            const response = await fetch(url);
            console.log(`✅ ${category} response status:`, response.status);
            const data = await response.json();
            console.log(`💬“¦ ${category} data:`, data);
            return { category, data };
          } catch (error) {
            console.error(`❌ Error fetching ${category}:`, error);
            return { category, data: null };
          }
        });

        // Wait for all fetches to complete
        const results = await Promise.all(fetchPromises);

        // Process results
        results.forEach(({ category, data }) => {
          if (data && data.matches && data.matches.length > 0) {
            console.log(`✅ ${category} has product:`, data.matches[0]);
            products[category] = data.matches[0];
          } else {
            console.log(`❌ ${category} has no products`);
            products[category] = null;
          }
        });

        console.log('💬“¦ Final trending products:', products);
        setTrendingProducts(products);
      } catch (error) {
        console.error('❌ Error fetching trending products:', error);
      } finally {
        setLoadingTrending(false);
      }
    };

    fetchTrendingProducts();
  }, []);


  // Handle file upload from camera
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const imagePreview = URL.createObjectURL(file);

    // Navigate to analyzing page first with smooth transition
    navigateWithTransition('/analyzing', {
      direction: 'forward',
      state: { image: imagePreview }
    });

    try {
      const results = await searchFashion(imagePreview);
      navigateWithTransition('/results', {
        direction: 'forward',
        state: { image: imagePreview, results }
      });
    } catch (error) {
      alert(error.message);
      navigateWithTransition('/', { direction: 'back' });
    }
  };

  // Camera action - open for all users (no auth required for search)
  const handleCameraClick = () => {
    document.getElementById('camera-input')?.click();
  };


  // Handle search - navigate to appropriate page
  const handleSearch = useCallback((query, mode) => {
    if (!query.trim()) return;

    if (mode === 'leo') {
      // Navigate to Leo chat with initial message
      navigateWithTransition('/chat/leo', {
        direction: 'forward',
        state: { initialMessage: query.trim() }
      });
    } else {
      // Navigate to text search page with query
      navigateWithTransition('/text-search', {
        direction: 'forward',
        state: { initialQuery: query.trim() }
      });
    }
  }, [navigateWithTransition]);

  // Handle image upload - go to results page (image search)
  const handleImageUpload = useCallback(async (file) => {
    const imagePreview = URL.createObjectURL(file);
    setIsSearching(true);

    try {
      const results = await searchFashion(imagePreview);
      // Navigate to results page for image search
      navigateWithTransition('/results', {
        direction: 'forward',
        state: { image: imagePreview, results, searchMode: 'image' }
      });
    } catch (error) {
      alert(error.message);
    } finally {
      setIsSearching(false);
    }
  }, [navigateWithTransition]);

  // Handle feature card clicks - navigate to appropriate page
  const handleCardClick = useCallback((cardId) => {
    switch (cardId) {
      case 'text-search':
        navigateWithTransition('/text-search', { direction: 'forward' });
        break;
      case 'leo-chat':
        navigateWithTransition('/chat/leo', { direction: 'forward' });
        break;
      case 'image-search':
        document.getElementById('camera-input')?.click();
        break;
      default:
        break;
    }
  }, [navigateWithTransition]);


  const scrollToTrending = (e) => {
    e.preventDefault();
    const trendingSection = document.getElementById('trending-styles');
    if (trendingSection) {
      trendingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Hidden File Input for Camera */}
      <input type="file" id="camera-input" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFileChange} />

      <main className="page-grid">

        {/* Hero Text + Button Section */}
        <section className="section hero-text-section">
            <div className="section-inner" style={{ textAlign: 'center' }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h1 style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>Style Search Engine</h1>
                  <p style={{ fontSize: '1rem', color: 'var(--text-muted)', marginBottom: '1rem', opacity: 0.8 }}>
                    Search fashion from around the world, all in one place
                  </p>
                  <p style={{ fontSize: '1.2rem', color: 'var(--text-muted)', marginBottom: '2rem' }}>
                    <TypewriterText
                      prefix="fantomize "
                      phrases={[
                        "your friend's amazing dress",
                        "with Leo, your stylist",
                        "the amazing wallpaper",
                        "anything. find your own style!"
                      ]}
                      typingSpeed={60}
                      deletingSpeed={30}
                      pauseDuration={2500}
                    />
                  </p>

                  {/* Feature Cards Row */}
                  <div className="feature-cards-row">
                    <SearchModeCard
                      type="LEO_CHAT"
                      onClick={handleCardClick}
                    />
                    <SearchModeCard
                      type="IMAGE_SEARCH"
                      onClick={handleCardClick}
                    />
                    <SearchModeCard
                      type="TEXT_SEARCH"
                      onClick={handleCardClick}
                    />
                  </div>
                </motion.div>
            </div>
        </section>

        {/* How It Works Demo Section */}
        <section className="section section-dark" id="how-it-works">
            <div className="section-inner">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>How It Works</h2>
                  <p style={{ textAlign: 'center', color: 'var(--text-muted)', marginBottom: '2rem' }}>
                    Find any style in 3 simple steps
                  </p>
                  <HowItWorksDemo />
                </motion.div>
            </div>
        </section>


        {/* Trending Styles Section (Converted from Music) */}
        <section className="section section-dark" id="trending-styles">
            <div className="section-inner">
                <h2>Trending Styles</h2>
                {loadingTrending ? (
                  <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading trending products...</p>
                ) : (
                  <div className="trending-grid-horizontal">
                    {Object.entries(trendingProducts).map(([category, product]) => {
                      console.log(`🎨 Rendering ${category}:`, product);
                      // Get display name from mapping (fallback to capitalized category)
                      const displayCategory = TRENDING_DISPLAY_NAMES[category] ||
                                             category.charAt(0).toUpperCase() + category.slice(1);

                      // If product has URL, make it clickable
                      const cardContent = (
                        <>
                          <h3 className="trending-card-title">{displayCategory}</h3>
                          {product ? (
                            <>
                              <div className="trending-card-image">
                                <img
                                  src={product.image_url || product.image || product.thumbnail || '/placeholder-product.svg'}
                                  alt={displayCategory}
                                  data-original-src={product.image_url || product.image || product.thumbnail}
                                  onError={(e) => handleImageError(e, product.image_url || product.image || product.thumbnail)}
                                />
                              </div>
                              <p className="trending-card-description">
                                {product.product_name || product.title || `${displayCategory} product`}
                              </p>
                              {product.price && (
                                <p className="trending-card-price">${product.price}</p>
                              )}
                            </>
                          ) : (
                            <>
                              <div className="trending-card-image trending-card-placeholder">
                                <span>No product found</span>
                              </div>
                              <p className="trending-card-description">Check back soon</p>
                            </>
                          )}
                        </>
                      );

                      // Wrap in link if product has URL
                      if (product && product.product_url) {
                        return (
                          <a
                            key={category}
                            href={product.product_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="trending-card trending-card-link"
                          >
                            {cardContent}
                          </a>
                        );
                      }

                      // Otherwise return non-clickable card
                      return (
                        <div key={category} className="trending-card">
                          {cardContent}
                        </div>
                      );
                    })}                  </div>
                )}
            </div>
        </section>

        {/* Blog Preview Section */}
        <section className="blog-preview-section" id="blog">
          <div className="section-container">
            <div className="section-header">
              <h2 className="section-title">From the Blog</h2>
              <p className="section-subtitle">Insights on visual search, AI, and the future of shopping</p>
            </div>
            <div className="blog-preview-grid">
              <Link to="/blog/visual-search-ai-tagging-ecommerce" className="blog-preview-card featured">
                <div className="blog-preview-image">
                  <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&q=80" alt="Visual Search" />
                </div>
                <div className="blog-preview-content">
                  <span className="blog-preview-category">Visual Search</span>
                  <h3>How Visual Search and AI Tagging Are Changing Product Discovery</h3>
                  <p>The gap between how people see products and how stores search products is one of the biggest friction points in online shopping.</p>
                </div>
              </Link>
              <Link to="/blog/new-way-of-shopping-visual-discovery" className="blog-preview-card">
                <div className="blog-preview-image">
                  <img src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=80" alt="Style Discovery" />
                </div>
                <div className="blog-preview-content">
                  <span className="blog-preview-category">Style & Discovery</span>
                  <h3>A New Way of Shopping: From Searching to Discovering Style</h3>
                </div>
              </Link>
            </div>
            <div className="blog-preview-cta">
              <Link to="/blog" className="blog-view-all">View All Articles →</Link>
            </div>
          </div>
        </section>

        <DisclaimerBanner />

      </main>

      <footer className="footer">
        <div className="footer-inner">
            <div className="footer-links">
                <Link to="/blog">Blog</Link>
                <a href="/about.html" target="_blank" rel="noopener noreferrer">About</a>
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
                <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a>
                <a href="/affiliate-disclosure.html" target="_blank" rel="noopener noreferrer">Affiliate Disclosure</a>
                <a href="/cookie-policy.html" target="_blank" rel="noopener noreferrer">Cookie Policy</a>
                <a href="/ai-disclaimer.html" target="_blank" rel="noopener noreferrer">AI Disclaimer</a>
            </div>
            <div className="footer-center">&copy; 2026 Fantomize. All rights reserved.</div>
         </div>
       </footer>

      <button className="floating-button" title="Open Camera" onClick={handleCameraClick}>
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {/* Outer hexagon */}
            <polygon points="50,5 93,27.5 93,72.5 50,95 7,72.5 7,27.5" />
            {/* Inner triangles - 6 triangles from center */}
            <line x1="50" y1="5" x2="50" y2="50" />
            <line x1="93" y1="27.5" x2="50" y2="50" />
            <line x1="93" y1="72.5" x2="50" y2="50" />
            <line x1="50" y1="95" x2="50" y2="50" />
            <line x1="7" y1="72.5" x2="50" y2="50" />
            <line x1="7" y1="27.5" x2="50" y2="50" />
        </svg>
      </button>
    </>
  );
};

export default Home;


